import * as THREE from "three";

/**
 * Atmosphere: drifting clouds + a loose flock of birds to make the sky feel
 * alive. Self-contained — no external assets (the cloud texture is drawn to a
 * canvas). Everything reads as distant background: clouds render behind the
 * tower and neither clouds nor birds cast shadows.
 *
 * Usage:
 *   const atmo = new Atmosphere(viewport.scene);
 *   viewport.onFrame((dt) => atmo.update(dt));
 *   // on teardown: atmo.dispose();
 */

// --- deterministic pseudo-random -------------------------------------------
// A tiny seeded generator so placement is reproducible across reloads without
// touching Math.random() at module scope. Each call advances an internal
// counter and hashes it through Math.sin (same trick the terrain noise uses).
function makeRng(seed: number): () => number {
  let n = seed;
  return () => {
    n += 1;
    const s = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
    return s - Math.floor(s); // 0..1
  };
}

// Spread over which clouds/birds wander before wrapping back around.
const FIELD = 620; // half-extent (m) of the horizontal region
const CLOUD_COUNT = 16;
const CLOUD_Y_MIN = 120;
const CLOUD_Y_MAX = 320;
const BIRD_COUNT = 18;

/** Soft radial-gradient "puff" texture, drawn once to a canvas. */
function makeCloudTexture(): THREE.Texture {
  const size = 128;
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const g = c.getContext("2d")!;
  const cx = size / 2;
  const cy = size / 2;
  const grad = g.createRadialGradient(cx, cy, 0, cx, cy, cx);
  grad.addColorStop(0.0, "rgba(255,255,255,0.95)");
  grad.addColorStop(0.35, "rgba(255,255,255,0.7)");
  grad.addColorStop(0.7, "rgba(255,255,255,0.22)");
  grad.addColorStop(1.0, "rgba(255,255,255,0.0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Build the geometry for one bird: two thin triangles forming a shallow "M/V".
 * The mesh's two wings are separate children so we can flap them by rotating
 * each about the bird's forward axis. Wings point along +X (span), bird flies
 * along +Z after we orient it per-frame.
 */
function makeWingGeometry(mirror: boolean): THREE.BufferGeometry {
  const dir = mirror ? -1 : 1;
  // A wing: root at centre, sweeping out and back to a tip, forming a triangle
  // pair (a flat delta) so it reads as a wing from any angle.
  const span = 1.0;
  const chord = 0.5;
  const verts = new Float32Array([
    0, 0, 0.15 * chord, // inner-front (at body)
    dir * span, 0, -0.35 * chord, // tip, swept back
    0, 0, -0.5 * chord, // inner-back
  ]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  geo.computeVertexNormals();
  return geo;
}

interface CloudSprite {
  sprite: THREE.Sprite;
  driftX: number; // per-second horizontal drift
  driftZ: number;
}

interface Bird {
  group: THREE.Group;
  leftWing: THREE.Mesh;
  rightWing: THREE.Mesh;
  // wandering state
  radius: number; // circling radius
  angle: number; // current angle around the circle
  angSpeed: number; // angular speed (rad/s)
  centerX: number;
  centerZ: number;
  baseY: number;
  bobAmp: number;
  bobPhase: number;
  bobSpeed: number;
  flapPhase: number;
  flapSpeed: number;
}

export class Atmosphere {
  private scene: THREE.Scene;
  private cloudTex: THREE.Texture;
  private cloudMat: THREE.SpriteMaterial;
  private clouds: CloudSprite[] = [];

  private birdMat: THREE.MeshStandardMaterial;
  private birdWingGeoL: THREE.BufferGeometry;
  private birdWingGeoR: THREE.BufferGeometry;
  private birds: Bird[] = [];

  private time = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    const rng = makeRng(1337);

    // --- clouds --------------------------------------------------------------
    this.cloudTex = makeCloudTexture();
    this.cloudMat = new THREE.SpriteMaterial({
      map: this.cloudTex,
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false, // don't occlude / write depth → read as background
      fog: true,
    });

    for (let i = 0; i < CLOUD_COUNT; i++) {
      const sprite = new THREE.Sprite(this.cloudMat);
      const size = 90 + rng() * 130; // 90..220 m across
      // clouds are wide & flat-ish
      sprite.scale.set(size, size * (0.45 + rng() * 0.2), 1);
      sprite.position.set(
        (rng() * 2 - 1) * FIELD,
        CLOUD_Y_MIN + rng() * (CLOUD_Y_MAX - CLOUD_Y_MIN),
        (rng() * 2 - 1) * FIELD,
      );
      sprite.renderOrder = -10; // draw before the tower
      sprite.castShadow = false;
      sprite.receiveShadow = false;
      // gentle, mostly-uniform wind with a little per-cloud variation
      const drift = 3.5 + rng() * 2.5; // m/s
      this.scene.add(sprite);
      this.clouds.push({ sprite, driftX: drift, driftZ: drift * 0.25 });
    }

    // --- birds ---------------------------------------------------------------
    this.birdMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a2f,
      roughness: 1.0,
      metalness: 0.0,
      side: THREE.DoubleSide,
      fog: true,
    });
    this.birdWingGeoL = makeWingGeometry(false);
    this.birdWingGeoR = makeWingGeometry(true);

    for (let i = 0; i < BIRD_COUNT; i++) {
      const group = new THREE.Group();
      const scale = 1.4 + rng() * 1.2; // small but visible against the sky
      group.scale.setScalar(scale);

      const leftWing = new THREE.Mesh(this.birdWingGeoL, this.birdMat);
      const rightWing = new THREE.Mesh(this.birdWingGeoR, this.birdMat);
      leftWing.castShadow = false;
      rightWing.castShadow = false;
      leftWing.receiveShadow = false;
      rightWing.receiveShadow = false;
      group.add(leftWing, rightWing);
      this.scene.add(group);

      const radius = 60 + rng() * 220;
      const angle = rng() * Math.PI * 2;
      // vary direction of circling (clockwise / anticlockwise)
      const dirSign = rng() < 0.5 ? -1 : 1;
      this.birds.push({
        group,
        leftWing,
        rightWing,
        radius,
        angle,
        angSpeed: dirSign * (0.05 + rng() * 0.12),
        centerX: (rng() * 2 - 1) * FIELD * 0.4,
        centerZ: (rng() * 2 - 1) * FIELD * 0.4,
        baseY: 70 + rng() * 200,
        bobAmp: 4 + rng() * 10,
        bobPhase: rng() * Math.PI * 2,
        bobSpeed: 0.3 + rng() * 0.5,
        flapPhase: rng() * Math.PI * 2,
        flapSpeed: 6 + rng() * 4, // rad/s of the flap oscillation
      });
    }
  }

  update(dt: number): void {
    this.time += dt;

    // --- clouds: constant drift, wrap when past the field bound --------------
    for (const c of this.clouds) {
      const p = c.sprite.position;
      p.x += c.driftX * dt;
      p.z += c.driftZ * dt;
      if (p.x > FIELD) p.x -= FIELD * 2;
      else if (p.x < -FIELD) p.x += FIELD * 2;
      if (p.z > FIELD) p.z -= FIELD * 2;
      else if (p.z < -FIELD) p.z += FIELD * 2;
    }

    // --- birds: slow circling + bob + wing flap -----------------------------
    for (const b of this.birds) {
      b.angle += b.angSpeed * dt;

      const x = b.centerX + Math.cos(b.angle) * b.radius;
      const z = b.centerZ + Math.sin(b.angle) * b.radius;
      const y = b.baseY + Math.sin(this.time * b.bobSpeed + b.bobPhase) * b.bobAmp;

      const pos = b.group.position;
      pos.set(x, y, z);

      // Face the direction of travel (tangent to the circle). Heading angle in
      // the XZ plane; +Z is the bird's forward axis, so rotate about Y.
      const tangent = b.angle + (b.angSpeed >= 0 ? Math.PI / 2 : -Math.PI / 2);
      b.group.rotation.y = tangent;

      // Wing flap: rotate each wing up/down about the forward (Z) axis, mirrored.
      b.flapPhase += b.flapSpeed * dt;
      const flap = Math.sin(b.flapPhase) * 0.55; // radians
      b.leftWing.rotation.z = flap;
      b.rightWing.rotation.z = -flap;
    }
  }

  dispose(): void {
    for (const c of this.clouds) {
      this.scene.remove(c.sprite);
    }
    this.clouds.length = 0;

    for (const b of this.birds) {
      this.scene.remove(b.group);
    }
    this.birds.length = 0;

    this.cloudMat.dispose();
    this.cloudTex.dispose();
    this.birdMat.dispose();
    this.birdWingGeoL.dispose();
    this.birdWingGeoR.dispose();
  }
}
